import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

export function DashboardPage() {
  useDocumentMeta({ title: 'Dashboard & UI: LogPulse Docs', description: 'Navigate the LogPulse dashboard: Log Explorer, saved searches, custom dashboards, and widgets.' });

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'log-explorer', label: 'Log Explorer' },
    { id: 'search-bar', label: 'Search Bar', level: 1 },
    { id: 'time-range-picker', label: 'Time Range Picker', level: 1 },
    { id: 'live-tail', label: 'Live Tail Mode', level: 1 },
    { id: 'field-sidebar', label: 'Field Sidebar', level: 1 },
    { id: 'log-detail-panel', label: 'Log Detail Panel', level: 1 },
    { id: 'saved-searches', label: 'Saved Searches' },
    { id: 'creating-saved-searches', label: 'Creating a Saved Search', level: 1 },
    { id: 'managing-saved-searches', label: 'Managing Saved Searches', level: 1 },
    { id: 'custom-dashboards', label: 'Custom Dashboards' },
    { id: 'creating-dashboards', label: 'Creating a Dashboard', level: 1 },
    { id: 'adding-widgets', label: 'Adding Widgets', level: 1 },
    { id: 'auto-refresh', label: 'Auto-Refresh', level: 1 },
    { id: 'widget-types', label: 'Widget Types' },
    { id: 'widget-configuration', label: 'Configuration Options', level: 1 },
    { id: 'filters-context', label: 'Filters & Context' },
    { id: 'persistent-filters', label: 'Persistent Filters', level: 1 },
    { id: 'context-switching', label: 'Context Switching', level: 1 },
    { id: 'keyboard-shortcuts', label: 'Keyboard Shortcuts' },
    { id: 'sharing', label: 'Sharing & Collaboration' },
    { id: 'share-urls', label: 'Shareable URLs', level: 1 },
    { id: 'export', label: 'Export to CSV / JSON', level: 1 },
    { id: 'team-dashboards', label: 'Team Dashboards', level: 1 },
    { id: 'themes', label: 'Dark Mode & Themes' },
    { id: 'mobile', label: 'Mobile & Responsive' },
  ];

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <Section id="overview" title="Dashboard & UI">
          <P>
            The LogPulse dashboard provides a unified interface for searching, analyzing, and
            visualizing your log data. It is built around three core components: the Log Explorer
            for ad-hoc search and investigation, Saved Searches for reusable queries, and Custom
            Dashboards for persistent monitoring views.
          </P>
          <P>
            All dashboard components support dark mode, responsive layouts, and real-time
            data updates. The interface is designed for both keyboard-driven workflows and
            point-and-click operation.
          </P>
        </Section>

        <Section id="log-explorer" title="Log Explorer">
          <P>
            The Log Explorer is the primary interface for searching and analyzing logs. It consists
            of a search bar, a results timeline, a log list, and a detail panel.
          </P>

          <SubSection id="search-bar" title="Search Bar">
            <P>
              The search bar accepts LPQL (LogPulse Query Language) queries. It provides
              syntax highlighting, auto-completion for field names and operators, and inline
              error reporting for invalid queries.
            </P>
            <CodeBlock title="Example LPQL queries">
{`# Filter by level and source
level=error source=api-gateway

# Full-text search with aggregation
"connection refused" | stats count by source

# Time-based filtering with field selection
source=auth-service | timechart count by level | limit 100`}
            </CodeBlock>
          </SubSection>

          <SubSection id="time-range-picker" title="Time Range Picker">
            <P>
              The time range picker sits in the top-right corner of the Log Explorer. It supports
              both relative ranges (last 15 minutes, last 1 hour, last 24 hours) and absolute
              ranges with a calendar picker. You can also type custom relative expressions such
              as "last 3h" or "last 7d".
            </P>
            <Table
              headers={['Preset', 'Duration', 'Use Case']}
              rows={[
                ['Last 15 minutes', '15m', 'Real-time debugging and tailing'],
                ['Last 1 hour', '1h', 'Recent incident investigation'],
                ['Last 4 hours', '4h', 'Shift-level monitoring'],
                ['Last 24 hours', '24h', 'Daily review and trend analysis'],
                ['Last 7 days', '7d', 'Weekly patterns and capacity planning'],
                ['Custom range', 'Variable', 'Specific incident windows or compliance audits'],
              ]}
            />
          </SubSection>

          <SubSection id="live-tail" title="Live Tail Mode">
            <P>
              Live Tail streams new log entries to your browser in real time. It is useful for
              monitoring deployments, debugging issues as they occur, or watching the output of
              a specific service. Click the Live Tail toggle in the search bar or press Alt+T
              to activate it.
            </P>
            <P>
              Live Tail respects your current LPQL query, so you can filter the stream to show
              only relevant entries. The stream pauses automatically when you scroll up to
              inspect a log entry and resumes when you scroll back to the bottom.
            </P>
            <Callout type="info">
              Live Tail uses WebSocket connections and consumes bandwidth proportional to your
              log throughput. For high-volume sources, add filters to reduce the stream volume.
            </Callout>
          </SubSection>

          <SubSection id="field-sidebar" title="Field Sidebar">
            <P>
              The field sidebar on the left side of the Log Explorer shows all fields detected
              in your current result set. Each field displays a distribution of its top values
              and the percentage of logs containing that field. Click a field value to add it
              as a filter, or click the field name to toggle column visibility in the log list.
            </P>
          </SubSection>

          <SubSection id="log-detail-panel" title="Log Detail Panel">
            <P>
              Click any log entry to open the detail panel. The panel shows the complete log
              record including all attributes, the raw JSON payload, and contextual links. From
              the detail panel you can copy the log ID, copy the raw JSON, or click "Show
              surrounding logs" to view logs from the same source within a narrow time window.
            </P>
          </SubSection>
        </Section>

        <Section id="saved-searches" title="Saved Searches">
          <P>
            Saved Searches let you store frequently used LPQL queries for quick access. Each
            saved search preserves the query text, the selected time range mode (relative or
            absolute), and any column layout customizations.
          </P>

          <SubSection id="creating-saved-searches" title="Creating a Saved Search">
            <P>
              1. Run your LPQL query in the Log Explorer.
            </P>
            <P>
              2. Click the Save button (or press Cmd+S / Ctrl+S) in the search bar.
            </P>
            <P>
              3. Enter a name and optional description for the saved search.
            </P>
            <P>
              4. Choose visibility: Private (only you) or Team (visible to all team members).
            </P>
            <P>
              5. Click Save.
            </P>
          </SubSection>

          <SubSection id="managing-saved-searches" title="Managing Saved Searches">
            <P>
              Access your saved searches from the Saved Searches tab in the left sidebar. You
              can edit, duplicate, or delete any search you own. Team searches can be edited by
              any team member with the Admin or Owner role.
            </P>
            <P>
              Saved searches can also be pinned to the top of the sidebar for quick access.
              Pinned searches appear in the order you pinned them and persist across sessions.
            </P>
          </SubSection>
        </Section>

        <Section id="custom-dashboards" title="Custom Dashboards">
          <P>
            Custom dashboards let you compose multiple widgets into a persistent monitoring
            view. Each dashboard has a configurable grid layout, auto-refresh interval, and
            shared time range.
          </P>

          <SubSection id="creating-dashboards" title="Creating a Dashboard">
            <P>
              Navigate to Dashboards in the left sidebar and click New Dashboard. Give it a name,
              set the default time range, and configure the auto-refresh interval (off, 30s, 1m,
              5m, or 15m). The dashboard starts with an empty canvas where you can add widgets.
            </P>
          </SubSection>

          <SubSection id="adding-widgets" title="Adding Widgets">
            <P>
              Click Add Widget to open the widget builder. Select a widget type, enter a LPQL
              query, configure the visualization options, and set the widget title. Widgets
              automatically inherit the dashboard time range but can be overridden individually.
            </P>
            <P>
              Widgets are arranged on a 12-column responsive grid. Drag widgets to reposition
              them, or drag their edges to resize. The minimum widget size is 3 columns wide
              and 2 rows tall.
            </P>
          </SubSection>

          <SubSection id="auto-refresh" title="Auto-Refresh">
            <P>
              When auto-refresh is enabled, all widgets on the dashboard re-execute their queries
              at the configured interval. The refresh cycle is staggered across widgets to
              avoid simultaneous query load. A countdown indicator in the dashboard header shows
              the time until the next refresh.
            </P>
          </SubSection>
        </Section>

        <Section id="widget-types" title="Widget Types">
          <P>
            LogPulse supports six widget types, each suited to different monitoring and
            analysis use cases.
          </P>
          <Table
            headers={['Widget', 'Description', 'Best For']}
            rows={[
              ['Counter', 'Displays a single numeric value from an aggregation query. Supports threshold-based coloring (green/yellow/red).', 'Error counts, request totals, active users'],
              ['Timechart', 'Line or area chart showing values over time. Supports multiple series, stacking, and logarithmic scale.', 'Throughput trends, error rate over time, latency percentiles'],
              ['Table', 'Tabular display of query results with sortable columns and pagination.', 'Top error messages, per-service breakdowns, recent events'],
              ['Pie Chart', 'Proportional display of categorical data. Supports up to 10 slices with an "Other" bucket.', 'Error distribution by type, traffic by region, log level breakdown'],
              ['Bar Chart', 'Horizontal or vertical bar chart for comparing values across categories.', 'Service comparison, per-environment error counts, top sources'],
              ['Stat Card', 'Compact card showing a value with a sparkline and trend indicator (up/down/flat arrow with percentage).', 'KPI monitoring, at-a-glance health metrics'],
            ]}
          />

          <SubSection id="widget-configuration" title="Configuration Options">
            <P>
              All widgets share a common set of configuration options:
            </P>
            <Table
              headers={['Option', 'Description', 'Default']}
              rows={[
                ['Title', 'Display title shown above the widget', 'Required'],
                ['LPQL Query', 'The query that provides data to the widget', 'Required'],
                ['Time Range Override', 'Override the dashboard time range for this widget', 'Inherit from dashboard'],
                ['Refresh Override', 'Override the dashboard refresh interval for this widget', 'Inherit from dashboard'],
                ['Thresholds', 'Color thresholds for Counter and Stat Card widgets (green/yellow/red boundaries)', 'None'],
                ['Legend Position', 'Position of the legend for chart widgets (top, bottom, right, hidden)', 'Bottom'],
                ['Max Series', 'Maximum number of series to display in chart widgets', '10'],
              ]}
            />
          </SubSection>
        </Section>

        <Section id="filters-context" title="Filters & Context">
          <SubSection id="persistent-filters" title="Persistent Filters">
            <P>
              Persistent filters apply to all queries across the Log Explorer and dashboards.
              They are useful for scoping your view to a specific environment, service, or
              deployment. Set persistent filters from the filter bar at the top of the page.
            </P>
            <P>
              Common persistent filter patterns include filtering by environment (production,
              staging, development), by service name, by region, or by deployment version.
              Persistent filters are saved to your user profile and apply across sessions.
            </P>
          </SubSection>

          <SubSection id="context-switching" title="Context Switching">
            <P>
              Use the context switcher in the top navigation bar to switch between environments
              or services. The context switcher updates all persistent filters simultaneously,
              allowing you to quickly move between different scopes without manually adjusting
              each filter.
            </P>
            <CodeBlock title="Context examples">
{`# Production context
environment=production

# Staging context for a specific service
environment=staging source=payment-service

# Regional context
environment=production attributes.region=eu-west-1`}
            </CodeBlock>
          </SubSection>
        </Section>

        <Section id="keyboard-shortcuts" title="Keyboard Shortcuts">
          <P>
            LogPulse supports keyboard shortcuts for common actions. All shortcuts are shown
            with macOS bindings below; on Windows and Linux, replace Cmd with Ctrl.
          </P>
          <Table
            headers={['Shortcut', 'Action', 'Context']}
            rows={[
              ['/', 'Focus the search bar', 'Global'],
              ['Cmd+K', 'Open the command palette', 'Global'],
              ['Cmd+Enter', 'Run the current LPQL query', 'Search bar'],
              ['Esc', 'Close the current panel or modal', 'Global'],
              ['Cmd+S', 'Save the current search', 'Log Explorer'],
              ['Alt+T', 'Toggle Live Tail mode', 'Log Explorer'],
              ['J / K', 'Navigate down / up in the log list', 'Log list'],
              ['Enter', 'Open the detail panel for the selected log', 'Log list'],
              ['Cmd+Shift+C', 'Copy the selected log as JSON', 'Detail panel'],
              ['Cmd+/', 'Show all keyboard shortcuts', 'Global'],
              ['Cmd+D', 'Toggle dark mode', 'Global'],
              ['Cmd+Shift+F', 'Toggle fullscreen mode', 'Dashboard'],
            ]}
          />
        </Section>

        <Section id="sharing" title="Sharing & Collaboration">
          <SubSection id="share-urls" title="Shareable URLs">
            <P>
              Every view in LogPulse generates a shareable URL that encodes the current query,
              time range, and column layout. Share these URLs with team members to provide
              exact context for an investigation. Relative time ranges are preserved in the URL,
              so a link with "last 1 hour" always shows the most recent hour when opened.
            </P>
            <P>
              To share a specific absolute time range (for example, during an incident), click
              the Share button and select "Lock time range." This generates a URL with absolute
              timestamps that always shows the same data.
            </P>
          </SubSection>

          <SubSection id="export" title="Export to CSV / JSON">
            <P>
              Export query results from the Log Explorer or any Table widget by clicking the
              Export button. Choose between CSV (for spreadsheet tools) or JSON (for programmatic
              processing). Exports include all columns visible in the current view and respect
              the active time range and filters.
            </P>
            <Callout type="info">
              Exports are limited to 100,000 rows. For larger exports, use the HTTP API with
              pagination or configure a scheduled export in Data Retention settings.
            </Callout>
          </SubSection>

          <SubSection id="team-dashboards" title="Team Dashboards">
            <P>
              Dashboards can be shared with your entire team by setting their visibility to
              Team. Team dashboards appear in a shared section of the Dashboards list and can
              be edited by any team member with the Admin or Owner role. Members and Viewers
              can view team dashboards but cannot modify them.
            </P>
          </SubSection>
        </Section>

        <Section id="themes" title="Dark Mode & Themes">
          <P>
            LogPulse supports light and dark color schemes. The theme can be set to Light, Dark,
            or System (follows your operating system preference). Toggle the theme using the
            sun/moon icon in the top navigation bar or the keyboard shortcut Cmd+D.
          </P>
          <P>
            All UI components, charts, and code blocks are designed with both themes in mind.
            Dashboard widgets automatically adapt their color palettes to maintain readability
            in both modes.
          </P>
          <Callout type="tip">
            For presentations or screen sharing, use the Light theme for better visibility on
            projectors. For extended monitoring sessions, the Dark theme reduces eye strain.
          </Callout>
        </Section>

        <Section id="mobile" title="Mobile & Responsive">
          <P>
            The LogPulse dashboard is fully responsive and works on tablets and mobile devices.
            On smaller screens, the layout automatically adjusts: the sidebar collapses into a
            slide-out drawer, dashboard widgets stack vertically, and the Log Explorer switches
            to a compact view.
          </P>
          <Table
            headers={['Breakpoint', 'Width', 'Layout Changes']}
            rows={[
              ['Desktop', '1280px and above', 'Full layout with sidebar, TOC, and multi-column dashboard grid'],
              ['Tablet', '768px to 1279px', 'Sidebar collapses, dashboard grid reduces to 2 columns, TOC hidden'],
              ['Mobile', 'Below 768px', 'Slide-out navigation, single-column layout, simplified log list'],
            ]}
          />
          <P>
            Touch gestures are supported for common interactions: swipe to dismiss panels,
            pinch to zoom on charts, and long-press to access context menus.
          </P>
        </Section>
      </div>

      <TableOfContents items={toc} />
    </div>
  );
}
